'use client'

import { format, parseISO } from 'date-fns'
import { AlertCircle, AlertTriangle, MessageCircle, RefreshCw, type LucideIcon } from 'lucide-react'

import type { RedeemMessageKey } from '../i18n/redeem.en'
import { useRedeemT } from '../i18n/useRedeemT'
import type { IRedeemErrorVariant, IRedeemQueryPrefill, IUsedGiftBooking } from '../types/schema'
import { buildLineHref, buildLineMessage } from '../utils/lineLink'
import { CTA_SECONDARY } from './styles'

interface Props {
  variant: IRedeemErrorVariant
  /** Populated only for the `used` branch — the booking that consumed the gift. */
  usedBooking?: IUsedGiftBooking | null
  giftNumber?: string
  prefill?: IRedeemQueryPrefill | null
}

/**
 * How bad it is — a different question from whether a human can help (`SHOW_LINE_CTA` below).
 * All nine variants in the same red made a dropped connection look as final as a voided gift.
 */
type Tone = 'critical' | 'caution' | 'info'

/**
 * Same shell in all three tones; only the icon carries colour. The tint is invisible on the white
 * card (~1.05:1), so the `-200` border is what makes it read as a block — not decoration.
 * `danger` = the brick from `redeem.scss`, so panel and field errors cannot drift.
 */
const TONES: Record<Tone, { Icon: LucideIcon; icon: string; panel: string }> = {
  critical: { Icon: AlertTriangle, icon: 'text-danger', panel: 'border-danger-200 bg-danger-50' },
  caution: { Icon: AlertCircle, icon: 'text-amber-700', panel: 'border-amber-200 bg-amber-50' },
  info: { Icon: RefreshCw, icon: 'text-gray-500', panel: 'border-gray-200 bg-gray-50' }
}

/**
 * New keys, not the legacy `ERROR_ALERT_GIFT_*` rows: those were swal-modal bodies with no separate
 * title, so they repeat the heading in every locale, and NOTFOUND's ": Click Here" tail (the swal
 * button) would be dead text next to the LINE button.
 */
const COPY: Record<
  IRedeemErrorVariant,
  { title: RedeemMessageKey; body: RedeemMessageKey; tone: Tone }
> = {
  'can_use': { title: 'ERROR_TITLE_NETWORK', body: 'ERROR_ALERT_NETWORK', tone: 'info' }, // unreachable
  'used': { title: 'ERROR_TITLE_GIFT_USED', body: 'ERROR_BODY_GIFT_USED', tone: 'critical' },
  // A wrong eGift ID is usually a typo, so this asks the user to look again rather than alarming them.
  'not_found': {
    title: 'ERROR_TITLE_GIFT_NOTFOUND',
    body: 'ERROR_BODY_GIFT_NOTFOUND',
    tone: 'caution'
  },
  'expired': {
    title: 'ERROR_TITLE_GIFT_EXPIRED',
    body: 'ERROR_BODY_GIFT_EXPIRED',
    tone: 'critical'
  },
  'void': { title: 'ERROR_TITLE_GIFT_VOID', body: 'ERROR_BODY_GIFT_VOID', tone: 'critical' },
  'network': { title: 'ERROR_TITLE_NETWORK', body: 'ERROR_ALERT_NETWORK', tone: 'info' },
  // May or may not have been spent, so it needs a human — but the booking is not known to have
  // failed, so do not panic the user.
  'booking-unconfirmed': {
    title: 'ERROR_TITLE_BOOKING_UNCONFIRMED',
    body: 'ERROR_BODY_BOOKING_UNCONFIRMED',
    tone: 'caution'
  },
  'rate-limited': {
    title: 'ERROR_TITLE_RATE_LIMITED',
    body: 'ERROR_ALERT_RATE_LIMITED',
    tone: 'info'
  },
  // Shown after a failed drag, so it must describe a missed attempt — not an unattempted one.
  'captcha-fail': { title: 'ERROR_TITLE_CAPTCHA', body: 'ERROR_CAPTCHA_FAILED', tone: 'info' }
}

/** Statuses where contacting a human is the next step. `booking-unconfirmed` because retrying is unsafe. */
const SHOW_LINE_CTA: IRedeemErrorVariant[] = [
  'not_found',
  'used',
  'expired',
  'void',
  'booking-unconfirmed'
]

export default function ErrorState({ variant, usedBooking, giftNumber, prefill }: Props) {
  const t = useRedeemT()
  const copy = COPY[variant]
  const { Icon, icon, panel } = TONES[copy.tone]

  // Null when neither LINE env var is set, so the CTA is dropped rather than rendered dead.
  // No `branchName`: spec D-7 retired that query param, so the message carries the raw `branch`
  // id, which the reservation team can still resolve.
  const lineHref = buildLineHref(buildLineMessage({ giftNumber, prefill, variant }))
  const showContact = SHOW_LINE_CTA.includes(variant) && Boolean(lineHref)

  return (
    <div role='alert' className={`mt-4 rounded-xl border p-4 md:p-5 ${panel}`}>
      <div className='flex items-start gap-3'>
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${icon}`} />
        <div className='min-w-0'>
          {/* Title and body stay neutral: the icon is the only coloured element in the panel. */}
          <h3 className='text-sm font-bold text-theme'>{t(copy.title)}</h3>
          <p className='mt-1 text-sm text-gray-700'>{t(copy.body)}</p>

          {usedBooking && <UsedBookingLine booking={usedBooking} />}

          {showContact && (
            // Plain anchor, not the locale-aware `Link` — this leaves the site for LINE.
            <a
              href={lineHref!}
              target='_blank'
              rel='noopener noreferrer'
              // Same pill as VerifySection's Gaaya link — no second button style.
              className={`${CTA_SECONDARY} mt-3 px-4 py-2`}>
              <MessageCircle className='h-3.5 w-3.5' />
              {t('BUTTON_CONTACT_LINE')}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function UsedBookingLine({ booking }: { booking: IUsedGiftBooking }) {
  const t = useRedeemT()
  const parsed = parseISO(booking.appt_date)
  const date = Number.isNaN(parsed.valueOf()) ? booking.appt_date : format(parsed, 'd MMM yyyy')

  return (
    <p className='mt-2 rounded-lg bg-white px-3 py-2 text-xs text-gray-700'>
      {t('ERROR_ALERT_GIFT_USED_DETAIL', {
        ref: String(booking.bpds_id),
        date,
        branch: booking.branch_long_name
      })}
    </p>
  )
}
